import type {StateCreator} from 'zustand'
import type {MarkersInfo, MarkersSlice} from './markers'
import {
  getSelectedAnnotationId,
  getSelectedCommentId,
  parsePathDigestWithoutLineNumbers,
} from '@github-ui/diffs/document-hash-helpers'
import type {DiffSummariesSlice, DiffSummariesSliceOpts} from './diff-summaries'
import type {ProgressiveDiffsSlice} from './progressive-diffs'

export interface MarkerAndDiffNavigationSlice {
  markerAndDiffNavigation: {
    markerId: string | undefined
    commentId: number | undefined // check to see if string
    getMarkerId: () => string | undefined
    getCommentId: () => number | undefined
  }
  markerAndDiffNavigationActions: {
    navigateToSelectedComment: (commentId: number) => void
    navigateToSelectedAnnotation: (annotationId: number) => void
    handleHashUpdate: () => void
  }
}

export interface MarkerAndDiffNavigationOpts {
  markers?: MarkersInfo | (undefined & Pick<DiffSummariesSliceOpts, 'initialDiffSummaries'>)
}

export const createMarkerAndDiffNavigationSlice =
  ({
    markers, // Markers are passed here to set the initial values. The store is not yet initialized.
  }: MarkerAndDiffNavigationOpts): StateCreator<
    MarkerAndDiffNavigationSlice & DiffSummariesSlice & MarkersSlice & ProgressiveDiffsSlice,
    [['zustand/devtools', never]],
    [],
    MarkerAndDiffNavigationSlice
  > =>
  (set, get) => {
    const selectedCommentId = getSelectedCommentId()
    const selectedAnnotationId = getSelectedAnnotationId()
    let markerId = undefined
    let commentId = undefined

    if (markers) {
      if (selectedCommentId) {
        const threads = Object.values(markers || {}).flatMap(files =>
          Object.values(files).flatMap(threadsInLine => {
            return threadsInLine?.threads ?? []
          }),
        )
        if (threads) {
          const selectedCommentIdNum = Number(selectedCommentId)
          for (const thread of Object.values(threads)) {
            const comments = thread?.commentsData?.comments
            if (thread && comments) {
              for (const comment of comments) {
                if (Number(comment.databaseId) === selectedCommentIdNum) {
                  markerId = thread.id
                  commentId = selectedCommentIdNum
                }
              }
            }
          }
        }
      } else if (selectedAnnotationId) {
        const annotations = Object.values(markers).flatMap(files =>
          Object.values(files).flatMap(annotationsInLine => {
            return annotationsInLine?.annotations ?? []
          }),
        )
        if (annotations) {
          const selectedAnnotationIdNum = Number(selectedAnnotationId)
          for (const annotation of Object.values(annotations)) {
            if (Number(annotation.databaseId) === selectedAnnotationIdNum) {
              markerId = annotation.id
            }
          }
        }
      }
    }
    return {
      markerAndDiffNavigation: {
        markerId,
        commentId,
        getCommentId: () => get().markerAndDiffNavigation.commentId,
        getMarkerId: () => get().markerAndDiffNavigation.markerId,
      },
      markerAndDiffNavigationActions: {
        navigateToSelectedComment: (targetCommentId: number) => {
          const thread = get().getThreadFromCommentID(targetCommentId)
          if (thread) {
            const threadPathDigest = get().getPathDigestFromThreadID(thread.id)
            get().progressiveDiffsActions.updateSelectedPathDigestAndFetch(threadPathDigest)
            set((prevState: MarkerAndDiffNavigationSlice) => ({
              markerAndDiffNavigation: {
                ...prevState.markerAndDiffNavigation,
                commentId: targetCommentId,
                markerId: thread?.id,
              },
            }))
          }
        },
        navigateToSelectedAnnotation: (annotationId: number) => {
          const annotation = get().getAnnotationFromID(annotationId)

          if (annotation) {
            const annotationPathDigest = get().getPathDigestFromAnnotationID(annotationId)
            get().progressiveDiffsActions.updateSelectedPathDigestAndFetch(annotationPathDigest)
            set((prevState: MarkerAndDiffNavigationSlice) => ({
              markerAndDiffNavigation: {
                ...prevState.markerAndDiffNavigation,
                commentId: undefined, // Clear comment ID when navigating to annotation
                markerId: annotation.id,
              },
            }))
          }
        },
        handleHashUpdate: () => {
          const urlCommentId = getSelectedCommentId()
          const urlAnnotationId = getSelectedAnnotationId()
          const urlPathDigest = parsePathDigestWithoutLineNumbers(window.location.hash)

          const currentState = get()
          const currentCommentId = currentState.markerAndDiffNavigation.commentId
          const currentMarkerId = currentState.markerAndDiffNavigation.markerId
          const currentPathDigest = currentState.selectedPathDigest

          // Navigate based on URL hash values, only if something has changed to avoid rerenders
          if (urlCommentId && urlCommentId !== currentCommentId) {
            currentState.markerAndDiffNavigationActions.navigateToSelectedComment(urlCommentId)
          } else if (urlAnnotationId) {
            // Navigate to the selected annotation
            const annotation = currentState.getAnnotationFromID(urlAnnotationId)
            const expectedMarkerId = annotation?.id

            if (expectedMarkerId && expectedMarkerId !== currentMarkerId) {
              currentState.markerAndDiffNavigationActions.navigateToSelectedAnnotation(urlAnnotationId)
            }
          } else if (urlPathDigest && urlPathDigest !== currentPathDigest) {
            // Navigate to the selected path digest
            currentState.progressiveDiffsActions.updateSelectedPathDigestAndFetch(urlPathDigest)
          } else if (!urlCommentId && !urlAnnotationId && !urlPathDigest && currentPathDigest) {
            // Clear selection if no hash is present but we have a current selection
            currentState.progressiveDiffsActions.updateSelectedPathDigestAndFetch(undefined)
          }
        },
      },
    }
  }
